import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadLanguage } from "../i18n/i18n";
import { isAppLocale, splitLocalePath, withLocale } from "../i18n/localePath";
import styles from "./LanguageSwitcher.module.css";

const OPTIONS = [
  { value: "en", name: "English", flag: "/flags/us.svg" },
  { value: "ru", name: "Русский", flag: "/flags/ru.svg" },
  { value: "uz", name: "Oʻzbek", flag: "/flags/uz.svg" },
];

function normalizeLang(code: string | undefined): string {
  const base = code?.split("-")[0]?.toLowerCase() ?? "en";
  return OPTIONS.some((o) => o.value === base) ? base : "en";
}

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const urlLocale = splitLocalePath(location.pathname).locale;
  const value = urlLocale ?? normalizeLang(i18n.language);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = OPTIONS.find((o) => o.value === value) ?? OPTIONS[0];

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Language: ${current.name}`}
        className={styles.trigger}
        style={{
          display: "flex",
          alignItems: "center",
          boxSizing: "border-box",
          gap: 4,
          padding: "0 8px",
          borderRadius: 8,
          border: "1px solid var(--app-border, #e5e7eb)",
          background: "var(--app-surface, #fff)",
          color: "var(--app-text, #374151)",
          cursor: "pointer",
          transition: "border-color 0.15s, box-shadow 0.15s",
          outline: "none",
          lineHeight: 1,
        }}
        onMouseEnter={(e) =>
          ((e.currentTarget as HTMLButtonElement).style.borderColor =
            "var(--app-brand, #7f56d9)")
        }
        onMouseLeave={(e) =>
          ((e.currentTarget as HTMLButtonElement).style.borderColor =
            "var(--app-border, #e5e7eb)")
        }
      >
        <img
          src={current.flag}
          alt=""
          width={18}
          height={13}
          style={{ borderRadius: 2, objectFit: "cover", display: "block" }}
        />
        <svg
          aria-hidden
          focusable={false}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0)",
            transition: "transform 0.15s",
            color: "var(--app-text-muted, #9ca3af)",
          }}
        >
          <path
            d="M2 4L6 8L10 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Languages"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: 52,
            background: "var(--app-surface, #fff)",
            border: "1px solid var(--app-border, #e5e7eb)",
            borderRadius: 10,
            boxShadow:
              "0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -1px rgba(0,0,0,0.04)",
            overflow: "hidden",
            zIndex: 200,
          }}
        >
          {OPTIONS.map((opt) => (
            <button
              type="button"
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              aria-label={opt.name}
              onClick={() => {
                setOpen(false);
                if (!isAppLocale(opt.value) || opt.value === value) return;
                const next = withLocale(
                  `${location.pathname}${location.search}${location.hash}`,
                  opt.value,
                );
                // Fetch the bundle first so the UI never flashes English.
                void loadLanguage(opt.value).then(async () => {
                  await i18n.changeLanguage(opt.value);
                  navigate(next);
                });
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                padding: "8px",
                border: "none",
                background:
                  opt.value === value
                    ? "var(--app-surface-muted, #f3f4f6)"
                    : "transparent",
                color: "var(--app-text, #374151)",
                cursor: "pointer",
                textAlign: "left",
                transition: "background 0.1s",
                lineHeight: 1,
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.background =
                  "var(--app-surface-muted, #f3f4f6)")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.background =
                  opt.value === value
                    ? "var(--app-surface-muted, #f3f4f6)"
                    : "transparent")
              }
            >
              <img
                src={opt.flag}
                alt=""
                width={16}
                height={11}
                style={{ borderRadius: 2, objectFit: "cover", display: "block" }}
              />
              {opt.value === value && (
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 12 12"
                  fill="none"
                  aria-hidden
                  focusable={false}
                  style={{ marginLeft: "auto", color: "var(--app-brand, #7f56d9)" }}
                >
                  <path
                    d="M2 6L5 9L10 3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
